<script lang="tsx">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'BlackListQuery',
})
</script>
<template>
  <div class="page">
    <ListPage ref="listRef" border :fetch="fetch" :columns="columns">
      <template #search>
        <el-button type="primary" @click="openAddDialog">添加黑名单</el-button>
      </template>
    </ListPage>

    <el-dialog v-model="addDialogVisible" title="添加黑名单">
      <el-form :model="addForm" label-width="80px">
        <el-form-item label="UID">
          <el-input v-model="addForm.uid" placeholder="请输入通行证uid" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleAdd" :loading="addLoading">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="tsx" name="BlackListQuery">
import { ElMessage } from 'element-plus';
import { ListPage } from 'emui-plus';
import { computed, inject, ref, watch } from 'vue';
import { postBlackListAdd, postBlackListDelete, postBlackListQueryForPage } from '@/api/modules/blackList';
import { getCompList } from '@/api/modules/comp';
import { type CompType } from '@/constants/compType';


const props = defineProps<{
  compType: CompType;
}>();

const listRef = ref<any>(null);
const $confirm = inject<(message: string) => Promise<void>>('$confirm');

const compOptions = ref<{ label: string; value: string }[]>([]);
const selectedCompId = ref<string>('');

const addDialogVisible = ref(false);
const addLoading = ref(false);
const addForm = ref<{ uid: string }>({ uid: '' });

const deletingUids = ref<Set<string>>(new Set());

const getCurrentCompId = () => {
  const searchFormData = listRef.value?.searchFormData;
  const currentFormData = searchFormData?.value ?? searchFormData ?? {};
  return currentFormData.compId ?? selectedCompId.value ?? '';
};

const columns = computed(() => [

  {
    prop: 'compId',
    label: '比赛',
    hide: true,
    search: {
      prop: 'compId',
      labelWidth: 80,
      formType: 'select',
      options: compOptions.value,
      filterable: true,
      clearable: false,
      placeholder: '请选择比赛',
      style: { width: '240px' },
    },
  },
  { prop: 'compName', label: '赛事名称', minWidth: 140 },
  { prop: 'orgName', label: '学校', minWidth: 140 },
  { prop: 'name', label: '姓名', minWidth: 100 },
  { prop: 'department', label: '院系', minWidth: 140 },
  { prop: 'nick', label: '通行证昵称', minWidth: 140 },
  { prop: 'starPhone', label: '手机号', minWidth: 120 },
  { prop: 'stuNum', label: '学号', minWidth: 120 },
  {
    prop: 'uid',
    label: 'UID',
    minWidth: 160,
    search: { prop: 'uid', labelWidth: 80, placeholder: '请输入UID' },
  },
  {
    type: 'operation',
    label: '操作',
    fixed: 'right',
    width: 120,
    align: 'center',
    render(data: any) {
      const uid = data?.row?.uid as string | undefined;
      return (
        <el-button
          type="danger"
          onClick={() => handleDelete(data.row)}
          loading={uid ? deletingUids.value.has(uid) : false}
        >
          删除
        </el-button>
      );
    },
  },
]);

const fetch = async (params: any) => {
  const compId = params.compId ?? getCurrentCompId();
  if (!compId) {
    return { list: [], total: 0 };
  }
  selectedCompId.value = String(compId);
  params.compId = Number(compId);
  const res = await postBlackListQueryForPage(params);
  return { list: res.records || [], total: res.total || 0 };
};

const openAddDialog = () => {
  addForm.value = { uid: '' };
  addDialogVisible.value = true;
};

const handleAdd = async () => {
  const compId = getCurrentCompId();
  if (!compId) {
    ElMessage.warning('请选择比赛');
    return;
  }
  if (!addForm.value.uid) {
    ElMessage.warning('请输入通行证uid');
    return;
  }
  addLoading.value = true;
  try {
    await postBlackListAdd({ compId: Number(compId), uid: addForm.value.uid });
    ElMessage.success('添加成功');
    addDialogVisible.value = false;
    listRef.value?.doSearch();
  } catch (error) {
    ElMessage.error('添加失败');
  } finally {
    addLoading.value = false;
  }
};

const handleDelete = async (row: any) => {
  const compId = getCurrentCompId();
  if (!compId) {
    ElMessage.warning('请选择比赛');
    return;
  }
  const uid = row?.uid;
  if (!uid) {
    ElMessage.warning('列表数据缺少uid，无法删除');
    return;
  }
  try {
    await $confirm?.('确定删除该黑名单吗？');
  } catch (error) {
    return;
  }
  deletingUids.value.add(uid);
  try {
    await postBlackListDelete({ compId: String(compId), uid });
    ElMessage.success('删除成功');
    listRef.value?.doSearch();
  } catch (error) {
    ElMessage.error('删除失败');
  } finally {
    deletingUids.value.delete(uid);
  }
};

const initCompSelect = async () => {
  if (!props.compType) {
    compOptions.value = [];
    selectedCompId.value = '';
    return;
  }

  try {
    const { records } = await getCompList({
      compType: props.compType,
      status: '1',
      current: 1,
      size: 99999999,
    });

    compOptions.value = (records || []).map(item => ({
      label: item.compName,
      value: String(item.compId),
    }));

    if (!compOptions.value.find(a => a.value === selectedCompId.value)) {
      selectedCompId.value = compOptions.value[0]?.value || '';
    }

    if (selectedCompId.value) {
      const searchFormData = listRef.value?.searchFormData;
      const currentFormData = searchFormData?.value ?? searchFormData ?? {};
      if (currentFormData.compId !== selectedCompId.value) {
        listRef.value?.setSearchForm({ ...currentFormData, compId: selectedCompId.value });
      }
      listRef.value?.doSearch();
    }
  } catch (error) {
    compOptions.value = [];
    selectedCompId.value = '';
  }
};
watch(
  () => props.compType,
  async () => {
    await initCompSelect();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.page {
  height: 100%;
  padding: 20px;
  .list-page {
    height: 100%;
  }
}
</style>
